import { isPenPath } from "@excalidraw/element";

import { getEditedPenPath } from "../../actions/actionPenPath";
import { actionDeleteSelected } from "../../actions/actionDeleteSelected";
import {
  penAddNode,
  penJoinNodes,
  penMakeCorner,
  penMakeCurve,
  penMergeNodes,
  penSeparateNodes,
  penSetDrawMode,
  penSetMoveMode,
  penToggleSnap,
} from "../../actions/actionPenPath";
import { t } from "../../i18n";
import { getShortcutKey } from "../../shortcut";
import {
  PenCornerIcon,
  PenCurveIcon,
  PenMergeNodesIcon,
  PenIcon,
  PlusIcon,
  SelectionIcon,
  TrashIcon,
  cutIcon,
  LinkIcon,
  magnetIcon,
} from "../icons";
import { Island } from "../Island";
import { ToolButton } from "../ToolButton";

import "./PenPathActions.scss";

import type { Action } from "../../actions/types";
import type { AppClassProperties, AppState, UIAppState } from "../../types";

type Props = {
  app: AppClassProperties;
  appState: UIAppState;
};

export const PenPathActions = ({ app, appState }: Props) => {
  const edited = getEditedPenPath(appState as AppState, app);
  if (!edited || !isPenPath(edited.element)) {
    return null;
  }
  const { indices } = edited;

  const run = (action: Action) => () => app.actionManager.executeAction(action);
  // single source of truth: the action's own predicate, same one that
  // gates keyboard shortcuts (`ActionManager.isActionEnabled`), so the
  // toolbar can never drift from what a shortcut would actually do.
  const enabled = (action: Action) => app.actionManager.isActionEnabled(action);

  return (
    <div data-testid="pen-path-actions">
      <Island className="PenPathActions" padding={1}>
        <div className="PenPathActions__group">
          <ToolButton
            type="button"
            icon={PenIcon}
            aria-label={t("labels.penPath.drawNodes")}
            title={`${t("labels.penPath.drawNodes")} (${getShortcutKey("P")})`}
            data-testid="pen-path-draw-nodes"
            selected={appState.penEditMode === "draw"}
            onClick={run(penSetDrawMode)}
          />
          <ToolButton
            type="button"
            icon={SelectionIcon}
            aria-label={t("labels.penPath.moveNodes")}
            title={`${t("labels.penPath.moveNodes")} (${getShortcutKey("M")})`}
            data-testid="pen-path-move-nodes"
            selected={appState.penEditMode === "move"}
            onClick={run(penSetMoveMode)}
          />
        </div>
        <div className="PenPathActions__group">
          <ToolButton
            type="button"
            icon={PlusIcon}
            aria-label={t("labels.penPath.addNode")}
            title={`${t("labels.penPath.addNode")} (${getShortcutKey(
              "Shift++",
            )})`}
            data-testid="pen-path-add-node"
            disabled={!enabled(penAddNode)}
            onClick={run(penAddNode)}
          />
          <ToolButton
            type="button"
            icon={TrashIcon}
            aria-label={t("labels.penPath.deleteNode")}
            title={`${t("labels.penPath.deleteNode")} (${getShortcutKey(
              "Delete",
            )})`}
            data-testid="pen-path-delete-node"
            // actionDeleteSelected has no top-level `predicate` (its guard
            // lives inline in `perform`), so isActionEnabled would always
            // report it enabled — kept on `indices.length` here instead of
            // drifting to an always-true check.
            disabled={!indices.length}
            onClick={run(actionDeleteSelected)}
          />
        </div>
        <div className="PenPathActions__group">
          <ToolButton
            type="button"
            icon={PenMergeNodesIcon}
            aria-label={t("labels.penPath.mergeNodes")}
            title={`${t("labels.penPath.mergeNodes")} (${getShortcutKey(
              "CtrlOrCmd+J",
            )})`}
            data-testid="pen-path-merge-nodes"
            disabled={!enabled(penMergeNodes)}
            onClick={run(penMergeNodes)}
          />
          <ToolButton
            type="button"
            icon={LinkIcon}
            aria-label={t("labels.penPath.joinNodes")}
            title={`${t("labels.penPath.joinNodes")} (${getShortcutKey("J")})`}
            data-testid="pen-path-join-nodes"
            disabled={!enabled(penJoinNodes)}
            onClick={run(penJoinNodes)}
          />
          <ToolButton
            type="button"
            icon={cutIcon}
            aria-label={t("labels.penPath.separateNodes")}
            title={`${t("labels.penPath.separateNodes")} (${getShortcutKey(
              "K",
            )})`}
            data-testid="pen-path-separate-nodes"
            disabled={!enabled(penSeparateNodes)}
            onClick={run(penSeparateNodes)}
          />
        </div>
        <div className="PenPathActions__group">
          <ToolButton
            type="button"
            icon={PenCornerIcon}
            aria-label={t("labels.penPath.makeCorner")}
            title={`${t("labels.penPath.makeCorner")} (${getShortcutKey("X")})`}
            data-testid="pen-path-make-corner"
            disabled={!enabled(penMakeCorner)}
            onClick={run(penMakeCorner)}
          />
          <ToolButton
            type="button"
            icon={PenCurveIcon}
            aria-label={t("labels.penPath.makeCurve")}
            title={`${t("labels.penPath.makeCurve")} (${getShortcutKey("C")})`}
            data-testid="pen-path-make-curve"
            disabled={!enabled(penMakeCurve)}
            onClick={run(penMakeCurve)}
          />
        </div>
        <div className="PenPathActions__group">
          <ToolButton
            type="button"
            icon={magnetIcon}
            aria-label={t("labels.penPath.snapToNodes")}
            title={`${t("labels.penPath.snapToNodes")} (${getShortcutKey(
              "CtrlOrCmd+'",
            )})`}
            data-testid="pen-path-snap-toggle"
            selected={appState.penSnapEnabled}
            onClick={run(penToggleSnap)}
          />
        </div>
      </Island>
    </div>
  );
};
